import React, { useState, useEffect } from "react"
import { Link, useParams } from "react-router-dom"
import {
  Table,
  Col,
  Row,
  UncontrolledAlert,
  Spinner,
} from "reactstrap"
import api from "API/Api"
import {
  AvForm,
  AvField,
  AvRadioGroup,
  AvRadio,
} from "availity-reactstrap-validation"
import RatechartFile from "./../../../../assets/Ratechart.csv"
import PageLoader from "./../../../../assets/loading/pageLoad1.gif"

const EditIncentiveChart = () => {
  const { id } = useParams()
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [pageLoad, setPageLoad] = useState(true)
  const [quantity, setQuantity] = useState(false)
  const [perLtr, setPerLtr] = useState(false)
  const [both, setBoth] = useState(false)
  const [ratePercentage, setRatePercentage] = useState(false)
  const [ts, setTs] = useState([])
  const [fatRate, setFatRate] = useState(false)
  const [snfRate, setSnfRate] = useState(false)
  const [tsRate, setTsRate] = useState(false)
  const [showTbl, setShowTbl] = useState(false)
  const [fats, setFats] = useState([])
  const [snfs, setSnfs] = useState([])
  const [minFat, setMinFat] = useState("")
  const [maxFat, setMaxFat] = useState("")
  const [minSnf, setMinSnf] = useState("")
  const [maxSnf, setMaxSnf] = useState("")
  const [fatIncrement, setFatIncrement] = useState("0.1")
  const [snfIncrement, setSnfIncrement] = useState("0.1")
  const [limitFatSnf, setLimitFatSnf] = useState("")
  const [incentive, setIncentive] = useState({})
  const [fatSnf, setFatSnf] = useState({})
  const [entries, setEntries] = useState("1")
  const [priceData, setPriceData] = useState([])
  const [importError, setImportError] = useState("")
  const [gridError, setGridError] = useState("")

  useEffect(() => {
    getInfo()
  }, [])

  const getInfo = async () => {
    try {
      const resp = await api.get("incentive-chart/" + id)
      const data = resp.data.data[0]
      setIncentive(data)
      calcChangeHandler(data.calculationType)
      if (String(data.minQuantity) === "1") setQuantity(true)
      if (data.limitFatSnf) setLimitFatSnf(String(data.limitFatSnf))

      // Parse fat_snf JSON
      let parsedFatSnf = {}
      if (data.fat_snf) {
        try {
          parsedFatSnf = typeof data.fat_snf === "string" ? JSON.parse(data.fat_snf) : data.fat_snf
        } catch (e) {
          console.error("Failed to parse fat_snf:", e)
        }
      }
      setFatSnf(parsedFatSnf)

      // Set range values
      if (data.minFat) setMinFat(data.minFat)
      if (data.maxFat) setMaxFat(data.maxFat)
      if (data.minSnf) setMinSnf(data.minSnf)
      if (data.maxSnf) setMaxSnf(data.maxSnf)

      // Load FAT/SNF range table rows if applicable
      if (data.fatRange) {
        try {
          const fatRangeData = typeof data.fatRange === "string" ? JSON.parse(data.fatRange) : data.fatRange
          if (Array.isArray(fatRangeData)) setTs(fatRangeData)
        } catch (e) { /* ignore */ }
      }
      if (data.snfRange) {
        try {
          const snfRangeData = typeof data.snfRange === "string" ? JSON.parse(data.snfRange) : data.snfRange
          if (Array.isArray(snfRangeData)) setTs(snfRangeData)
        } catch (e) { /* ignore */ }
      }

      // Auto-generate grid if FAT&SNF type with existing data
      if (String(data.calculationType) === "2" && data.minFat && data.maxFat && data.minSnf && data.maxSnf) {
        generateGridFromData(data.minFat, data.maxFat, data.minSnf, data.maxSnf, "0.1", "0.1", parsedFatSnf)
      }
    } catch (error) {
      console.error("Failed to load incentive chart:", error)
    }
    setPageLoad(false)
  }

  const generateGridFromData = (mnFat, mxFat, mnSnf, mxSnf, fatInc, snfInc, savedData) => {
    const minF = parseFloat(mnFat)
    const maxF = parseFloat(mxFat)
    const minS = parseFloat(mnSnf)
    const maxS = parseFloat(mxSnf)
    const fInc = parseFloat(fatInc) || 0.1
    const sInc = parseFloat(snfInc) || 0.1

    if (isNaN(minF) || isNaN(maxF) || isNaN(minS) || isNaN(maxS)) return

    let fat = []
    for (let i = minF; i <= maxF + 0.001; i += fInc) {
      fat.push(i.toFixed(2))
    }
    let snf = []
    for (let i = minS; i <= maxS + 0.001; i += sInc) {
      snf.push(i.toFixed(2))
    }
    setFats(fat)
    setSnfs(snf)
    setFatSnf(savedData || {})
    setShowTbl(true)
  }

  const handleValidSubmit = async (event, values) => {
    setLoading(true)
    setSubmitted(false)
    try {
      // Add range values
      values.minFat = minFat
      values.maxFat = maxFat
      values.minSnf = minSnf
      values.maxSnf = maxSnf
      const response = await api.put("update-incentive-chart/" + id, values)
      if (response.data.success === true) {
        setSubmitted(true)
        setTimeout(() => setSubmitted(false), 3000)
      }
    } catch (error) {
      console.error("Update failed:", error)
    }
    setLoading(false)
  }

  const minQuantityHandler = e => {
    setQuantity(e.target.value === "1")
  }

  const calcChangeHandler = e => {
    const val = typeof e === "object" ? e.target.value : String(e)
    setPerLtr(val === "1")
    setBoth(val === "2")
    setFatRate(val === "3")
    setSnfRate(val === "4")
    setTsRate(val === "5")
    setRatePercentage(val === "6")
  }

  const addRow = e => {
    e.preventDefault()
    setTs([...ts, { from: "", upto: "", rate: "" }])
  }

  const deleteRow = (e, i) => {
    const data = [...ts]
    data.splice(i, 1)
    setTs(data)
  }

  const updateTsRow = (i, field, value) => {
    const data = [...ts]
    data[i] = { ...data[i], [field]: value }
    setTs(data)
  }

  const calculatePrice = () => {
    const minF = parseFloat(minFat)
    const maxF = parseFloat(maxFat)
    const minS = parseFloat(minSnf)
    const maxS = parseFloat(maxSnf)
    const fInc = parseFloat(fatIncrement) || 0.1
    const sInc = parseFloat(snfIncrement) || 0.1

    if (isNaN(minF) || isNaN(maxF) || isNaN(minS) || isNaN(maxS)) {
      setGridError("Enter valid numeric FAT/SNF range values.")
      return
    }
    if (minF >= maxF || minS >= maxS) {
      setGridError("Min values must be less than Max values.")
      return
    }
    const fatCount = Math.floor((maxF - minF) / fInc) + 1
    const snfCount = Math.floor((maxS - minS) / sInc) + 1
    if (fatCount > 200 || snfCount > 200) {
      setGridError("Grid too large (max 200 x 200). Reduce range or increase increment.")
      return
    }

    setGridError("")
    generateGridFromData(minFat, maxFat, minSnf, maxSnf, fatIncrement, snfIncrement, fatSnf)
  }

  function fileChangeHandler(e) {
    const file = e.target.files[0]
    if (!file) return
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setImportError("Only CSV files are allowed.")
      e.target.value = ""
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setImportError("File too large. Max 5MB.")
      e.target.value = ""
      return
    }
    setImportError("")
    const reader = new FileReader()
    reader.onload = function (evt) {
      const text = evt.target.result.replace(/\r\n/g, "\n").replace(/\r/g, "\n")
      const lines = text.split("\n").filter(l => l.trim())
      if (lines.length < 2) {
        setImportError("CSV must have header + at least one data row.")
        return
      }
      const headers = lines[0].split(",")
      const result = []
      for (let i = 1; i < lines.length; i++) {
        const currentline = lines[i].split(",")
        const obj = {}
        for (let j = 0; j < headers.length; j++) {
          obj[headers[j].trim()] = (currentline[j] || "").trim()
        }
        result.push(obj)
      }
      setPriceData(result)
    }
    reader.readAsText(file)
  }

  // Export grid data as CSV
  const exportGridCSV = () => {
    if (!fats.length || !snfs.length) return
    let csv = "FAT/SNF," + snfs.join(",") + "\n"
    fats.forEach(fat => {
      let row = fat
      snfs.forEach(snf => {
        const val = fatSnf[fat] && fatSnf[fat][snf] ? fatSnf[fat][snf] : ""
        row += "," + val
      })
      csv += row + "\n"
    })
    const blob = new window.Blob([csv], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = (incentive.chartName || "incentive_chart") + "_export.csv"
    a.click()
    window.URL.revokeObjectURL(url)
  }

  // Get grid cell value safely
  const getCellValue = (fat, snf) => {
    try {
      if (fatSnf && fatSnf[fat] && fatSnf[fat][snf] !== undefined) {
        return fatSnf[fat][snf]
      }
    } catch (e) { /* ignore */ }
    return ""
  }

  if (pageLoad) {
    return (
      <div className="pageLoader">
        <img src={PageLoader} alt="Loading..." />
      </div>
    )
  }

  return (
    <>
      <div className="dashBody">
        <div className="font-size-18 mb-0 dashHeader">
          <Row>
            <Col md={6}>
              <h4>Edit Incentive Chart</h4>
            </Col>
            <Col md={6} style={{ textAlign: "right" }}>
              <Link to="/incentive-charts" className="dashBtn">
                <i className=" fas fa-chevron-left "></i> <b> Back</b>
              </Link>
            </Col>
          </Row>
        </div>
        <div className="dashContent">
          <AvForm
            style={{ position: "relative" }}
            className="form-horizontal"
            onValidSubmit={(e, v) => handleValidSubmit(e, v)}
          >
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <AvField
                    name="chartName"
                    label="Incentive Chart Name"
                    value={incentive.chartName}
                    className="form-control"
                    type="text"
                    required
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <AvField
                    type="select"
                    name="calculationType"
                    value={String(incentive.calculationType)}
                    label="Calculation Type"
                    required
                    onChange={e => calcChangeHandler(e)}
                  >
                    <option value="">Select Calculation Type</option>
                    <option value="1">Quantity Based</option>
                    <option value="2">FAT & SNF</option>
                    <option value="3">Fat Range</option>
                    <option value="4">SNF Range</option>
                    <option value="5">TS Range</option>
                    <option value="6">Rate Percentage</option>
                  </AvField>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <AvRadioGroup
                    inline name="minQuantity"
                    value={String(incentive.minQuantity)}
                    label="Minimum Quantity Required"
                    required errorMessage="Pick one!"
                    onChange={e => minQuantityHandler(e)}
                  >
                    <AvRadio label="Yes" value="1" />
                    <AvRadio label="No" value="0" />
                  </AvRadioGroup>
                </div>
              </Col>
              {quantity ? (
                <>
                  <Col md={6}>
                    <div className="mb-3">
                      <AvField
                        name="applyIncentive"
                        label="Apply Incentive if Liters Greater Than"
                        value={incentive.applyIncentive}
                        className="form-control" type="text" required
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <AvRadioGroup
                        inline name="basedOn"
                        value={String(incentive.basedOn)}
                        label="Apply Liters Based On"
                        required errorMessage="Pick one!"
                      >
                        <AvRadio label="Shiftwise" value="1" />
                        <AvRadio label="Daywise" value="2" />
                      </AvRadioGroup>
                    </div>
                  </Col>
                </>
              ) : (
                <>
                  <AvField name="applyIncentive" value="0" type="hidden" />
                  <AvField name="basedOn" value="0" type="hidden" />
                </>
              )}

              {perLtr ? (
                <Col md={6}>
                  <div className="mb-3">
                    <AvField
                      name="incentivePerLtr"
                      label="Incentive Per Liter"
                      value={incentive.incentivePerLtr}
                      className="form-control" type="text" required
                    />
                  </div>
                </Col>
              ) : null}

              <Col md={6}>
                <div className="mb-3">
                  <AvRadioGroup
                    inline name="entries" label="Entries"
                    value={entries}
                    onChange={e => setEntries(e.target.value)}
                    required errorMessage="Pick one!"
                  >
                    <AvRadio label="Manual" value="1" />
                    <AvRadio label="File Upload" value="2" />
                  </AvRadioGroup>
                </div>
              </Col>

              {both ? (
                <>
                  {entries === "2" ? (
                    <Col md={6}>
                      <div className="mb-3">
                        <AvField
                          name="file" label="Upload CSV"
                          accept=".csv"
                          onChange={e => fileChangeHandler(e)}
                          className="form-control" type="file"
                        />
                        <AvField name="fat_snf" value={priceData} className="form-control" type="hidden" />
                        <p>
                          Sample CSV file{" "}
                          <Link to={RatechartFile} target="_blank" download>Download</Link>
                        </p>
                        {importError && (
                          <UncontrolledAlert color="danger">
                            <strong>⚠ </strong>{importError}
                          </UncontrolledAlert>
                        )}
                      </div>
                    </Col>
                  ) : null}

                  <Col md={6}>
                    <div className="mb-3">
                      <AvRadioGroup
                        inline name="status" label="Status"
                        value={String(incentive.status)}
                        required errorMessage="Pick one!"
                      >
                        <AvRadio label="Active" value="1" />
                        <AvRadio label="Inactive" value="0" />
                      </AvRadioGroup>
                    </div>
                  </Col>

                  {entries === "1" ? (
                    <>
                      <Col md={12}>
                        <div className="mb-3">
                          <Row>
                            <Col md={1}><label>FAT Range</label></Col>
                            <Col md={2}>
                              <AvField name="fat_min" value={minFat}
                                onChange={e => setMinFat(e.target.value)}
                                className="form-control" type="text" placeholder="Min" required />
                            </Col>
                            <Col md={2}>
                              <AvField name="fat_max" value={maxFat}
                                onChange={e => setMaxFat(e.target.value)}
                                className="form-control" type="text" placeholder="Max" required />
                            </Col>
                            <Col md={1}><label>Increment</label></Col>
                            <Col md={2}>
                              <AvField name="fat_inc" className="form-control"
                                type="text" value={fatIncrement}
                                onChange={e => setFatIncrement(e.target.value)} />
                            </Col>
                          </Row>
                        </div>
                      </Col>
                      <Col md={12}>
                        <div className="mb-3">
                          <Row>
                            <Col md={1}><label>SNF Range</label></Col>
                            <Col md={2}>
                              <AvField name="snf_min" value={minSnf}
                                onChange={e => setMinSnf(e.target.value)}
                                className="form-control" type="text" placeholder="Min" required />
                            </Col>
                            <Col md={2}>
                              <AvField name="snf_max" value={maxSnf}
                                onChange={e => setMaxSnf(e.target.value)}
                                className="form-control" type="text" placeholder="Max" required />
                            </Col>
                            <Col md={1}><label>Increment</label></Col>
                            <Col md={2}>
                              <AvField name="snf_inc" className="form-control"
                                type="text" value={snfIncrement}
                                onChange={e => setSnfIncrement(e.target.value)} />
                            </Col>
                          </Row>
                        </div>
                        <button className="btn btn-primary me-2" id="genarate" type="button"
                          onClick={() => calculatePrice()}>
                          Generate Grid
                        </button>
                        {showTbl && fats.length > 0 && (
                          <button className="btn btn-success" type="button" onClick={exportGridCSV}>
                            <i className="fas fa-file-csv"></i> Export CSV
                          </button>
                        )}
                        {gridError && (
                          <UncontrolledAlert color="danger" className="mt-2">
                            <strong>⚠ </strong>{gridError}
                          </UncontrolledAlert>
                        )}
                      </Col>
                    </>
                  ) : null}
                </>
              ) : null}

              {showTbl && entries === "1" && fats.length > 0 ? (
                <Col md={12} className="mt-3">
                  <div className="table-responsive" style={{ maxHeight: "500px", overflow: "auto" }}>
                    <Table className="table table-bordered table-sm" style={{ fontSize: "12px" }}>
                      <thead>
                        <tr style={{ position: "sticky", top: 0, background: "#f8f9fa", zIndex: 2 }}>
                          <th style={{ position: "sticky", left: 0, background: "#e9ecef", zIndex: 3 }}>FAT/SNF</th>
                          {snfs.map(snf => (
                            <th key={snf} style={{ minWidth: "70px", textAlign: "center" }}>{snf}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {fats.map(fat => (
                          <tr key={fat}>
                            <th style={{ position: "sticky", left: 0, background: "#e9ecef", zIndex: 1 }}>{fat}</th>
                            {snfs.map(snf => (
                              <td key={snf} style={{ padding: "2px" }}>
                                <AvField
                                  className="form-control form-control-sm"
                                  value={getCellValue(fat, snf)}
                                  type="text"
                                  name={"fat_snf[" + fat + "][" + snf + "]"}
                                  style={{ minWidth: "60px", fontSize: "11px", padding: "2px 4px" }}
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Col>
              ) : null}

              {ratePercentage ? (
                <Col md={6}>
                  <div className="mb-3">
                    <AvField
                      name="ratePercentage" label="Rate Percentage"
                      value={incentive.ratePercentage}
                      className="form-control" type="text" required
                    />
                  </div>
                </Col>
              ) : null}

              {fatRate ? (
                <>
                  <h4 className="mt-3">FAT Range Items</h4>
                  <Table className="table mb-0">
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th>FAT Greater Than</th>
                        <th>FAT Up To</th>
                        <th>Rate</th>
                        <th>
                          <button type="button" className="btn btn-primary btn-sm" onClick={e => addRow(e)}>
                            <i className="fa fa-plus"></i>
                          </button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {ts.map((t, i) => (
                        <tr key={i}>
                          <td>{i + 1}</td>
                          <td>
                            <AvField name={"fatRange[" + i + "][from]"} value={t.from || ""}
                              onChange={e => updateTsRow(i, "from", e.target.value)}
                              className="form-control" type="text" required />
                          </td>
                          <td>
                            <AvField name={"fatRange[" + i + "][upto]"} value={t.upto || ""}
                              onChange={e => updateTsRow(i, "upto", e.target.value)}
                              className="form-control" type="text" required />
                          </td>
                          <td>
                            <AvField name={"fatRange[" + i + "][rate]"} value={t.rate || ""}
                              onChange={e => updateTsRow(i, "rate", e.target.value)}
                              className="form-control" type="text" required />
                          </td>
                          <td>
                            <i className="fas fa-trash-alt icons icon-delete" onClick={e => deleteRow(e, i)}></i>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              ) : null}

              {snfRate ? (
                <>
                  <h4 className="mt-3">SNF Range Items</h4>
                  <Table className="table mb-0">
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th>SNF Greater Than</th>
                        <th>SNF Up To</th>
                        <th>Rate</th>
                        <th>
                          <button type="button" className="btn btn-primary btn-sm" onClick={e => addRow(e)}>
                            <i className="fa fa-plus"></i>
                          </button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {ts.map((t, i) => (
                        <tr key={i}>
                          <td>{i + 1}</td>
                          <td>
                            <AvField name={"snfRange[" + i + "][from]"} value={t.from || ""}
                              onChange={e => updateTsRow(i, "from", e.target.value)}
                              className="form-control" type="text" required />
                          </td>
                          <td>
                            <AvField name={"snfRange[" + i + "][upto]"} value={t.upto || ""}
                              onChange={e => updateTsRow(i, "upto", e.target.value)}
                              className="form-control" type="text" required />
                          </td>
                          <td>
                            <AvField name={"snfRange[" + i + "][rate]"} value={t.rate || ""}
                              onChange={e => updateTsRow(i, "rate", e.target.value)}
                              className="form-control" type="text" required />
                          </td>
                          <td>
                            <i className="fas fa-trash-alt icons icon-delete" onClick={e => deleteRow(e, i)}></i>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              ) : null}

              {tsRate ? (
                <>
                  <Col md={6}>
                    <AvRadioGroup
                      inline name="limitFatSnf"
                      value={String(incentive.limitFatSnf)}
                      label="Limit Minimum and Maximum FAT, SNF Values?"
                      required errorMessage="Pick one!"
                      onChange={e => setLimitFatSnf(e.target.value)}
                    >
                      <AvRadio label="Yes" value="1" />
                      <AvRadio label="No" value="0" />
                    </AvRadioGroup>
                  </Col>
                  {limitFatSnf === "1" ? (
                    <>
                      <Col md={6}>
                        <div className="mb-3">
                          <AvField name="minFat" value={incentive.minFat}
                            label="Minimum FAT Eligible" className="form-control" type="text" required />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <AvField name="maxFat" value={incentive.maxFat}
                            label="Maximum FAT Limit" className="form-control" type="text" required />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <AvField name="minSnf" value={incentive.minSnf}
                            label="Minimum SNF Eligible" className="form-control" type="text" required />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <AvField name="maxSnf" value={incentive.maxSnf}
                            label="Maximum SNF Limit" className="form-control" type="text" required />
                        </div>
                      </Col>
                    </>
                  ) : null}
                  <h4 className="mt-3">TS Range Items</h4>
                  <Table className="table mb-0">
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th>TS Greater Than</th>
                        <th>TS Up To</th>
                        <th>Rate</th>
                        <th>
                          <button type="button" className="btn btn-primary btn-sm" onClick={e => addRow(e)}>
                            <i className="fa fa-plus"></i>
                          </button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {ts.map((t, i) => (
                        <tr key={i}>
                          <td>{i + 1}</td>
                          <td>
                            <AvField name={"tsRange[" + i + "][from]"} value={t.from || ""}
                              onChange={e => updateTsRow(i, "from", e.target.value)}
                              className="form-control" type="text" required />
                          </td>
                          <td>
                            <AvField name={"tsRange[" + i + "][upto]"} value={t.upto || ""}
                              onChange={e => updateTsRow(i, "upto", e.target.value)}
                              className="form-control" type="text" required />
                          </td>
                          <td>
                            <AvField name={"tsRange[" + i + "][rate]"} value={t.rate || ""}
                              onChange={e => updateTsRow(i, "rate", e.target.value)}
                              className="form-control" type="text" required />
                          </td>
                          <td>
                            <i className="fas fa-trash-alt icons icon-delete" onClick={e => deleteRow(e, i)}></i>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              ) : null}
            </Row>

            <div className="mb-3 row mt-4">
              <div className="col-6">
                {submitted ? (
                  <UncontrolledAlert color="success">
                    <strong>Incentive Chart Updated Successfully!</strong>
                  </UncontrolledAlert>
                ) : null}
              </div>
              <div className="col-6 text-end">
                <button
                  className="btn btn-primary w-md waves-effect waves-light"
                  type="submit" disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" />
                      <span style={{ paddingLeft: "10px" }}>Updating...</span>
                    </>
                  ) : (
                    <span>Update Incentive Chart</span>
                  )}
                </button>
              </div>
            </div>
          </AvForm>
        </div>
      </div>
    </>
  )
}

export default EditIncentiveChart
